"use strict";
/**
 * Orderbook Module
 * Handles polling orderbook data and displaying live bid/ask prices
 * Supports both Kalshi (WS-backed cache via server) and Polymarket (WebSocket via server)
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.convertPolymarketToKalshiFormat = convertPolymarketToKalshiFormat;
exports.parseDisplayOrderbook = parseDisplayOrderbook;
exports.parseOrderbook = parseOrderbook;
exports.fetchDisplayOrderbook = fetchDisplayOrderbook;
exports.startOrderbookPolling = startOrderbookPolling;
exports.startKalshiOrderbookPolling = startKalshiOrderbookPolling;
exports.startProviderAwareOrderbookPolling = startProviderAwareOrderbookPolling;
const api_1 = require("./api");
const state_1 = require("./state");
const displayed_ask_history_1 = require("./orders/displayed-ask-history");
const kalshi_1 = require("./api/kalshi");
const orderbook_display_1 = require("../shared/orderbook-display");
const price_math_1 = require("../shared/price-math");
const registry_1 = require("./venues/registry");
const DEFAULT_KALSHI_POLLING_INTERVAL_MS = 250;
/**
 * Convert Polymarket orderbook format to Kalshi-compatible OrderbookData format.
 * Polymarket provides a single asset orderbook (YES token):
 * - bids/asks are { price: quantity } objects
 * - NO side is derived: NO bid = 1 - YES ask, NO ask = 1 - YES bid
 *
 * @param polymarketResponse The Polymarket API response
 * @returns OrderbookData compatible with existing parseOrderbook() function
 */
function convertPolymarketToKalshiFormat(polymarketResponse) {
    try {
        const { bids, asks } = polymarketResponse.data;
        // One-sided books are valid — only bail when there is nothing at all.
        if ((!bids || Object.keys(bids).length === 0) &&
            (!asks || Object.keys(asks).length === 0)) {
            console.warn("Polymarket orderbook data is empty");
            return null;
        }
        // Convert YES bids: { "0.54": "130" } → [["0.54", 130]]
        const yesBids = Object.entries(bids ?? {})
            .map(([price, quantity]) => [price, parseFloat(quantity)])
            .sort((a, b) => parseFloat(b[0]) - parseFloat(a[0])); // Highest price first
        // Convert YES asks: { "0.55": "10245.56" } → [["0.55", 10245.56]]
        const yesAsks = Object.entries(asks ?? {})
            .map(([price, quantity]) => [price, parseFloat(quantity)])
            .sort((a, b) => parseFloat(a[0]) - parseFloat(b[0])); // Lowest price first
        // Derive NO side from YES side
        // NO bid at price P = YES ask at (1 - P)
        // NO ask at price P = YES bid at (1 - P)
        const noBids = yesAsks
            .map(([yesAskPrice, quantity]) => {
            const noBidPrice = (1 - parseFloat(yesAskPrice)).toFixed(4);
            return [noBidPrice, quantity];
        })
            .sort((a, b) => parseFloat(b[0]) - parseFloat(a[0])); // Highest price first
        const noAsks = yesBids
            .map(([yesBidPrice, quantity]) => {
            const noAskPrice = (1 - parseFloat(yesBidPrice)).toFixed(4);
            return [noAskPrice, quantity];
        })
            .sort((a, b) => parseFloat(a[0]) - parseFloat(b[0])); // Lowest price first
        return {
            yes_dollars: yesBids,
            no_dollars: noBids,
        };
    }
    catch (error) {
        console.error("Error converting Polymarket orderbook format:", error);
        return null;
    }
}
/**
 * Assemble BidAskData from a display orderbook: best prices as numbers
 * (null per empty side), and cache them in state for fast order placement.
 * Returns null only when every column is empty.
 */
function displayToBidAsk(display, serverAgeMs) {
    const toPrice = (best) => best !== null ? parseFloat(best) : null;
    const yesBid = toPrice(display.yesBid.best);
    const yesAsk = toPrice(display.yesAsk.best);
    const noBid = toPrice(display.noBid.best);
    const noAsk = toPrice(display.noAsk.best);
    if (yesBid === null && yesAsk === null && noBid === null && noAsk === null) {
        return null;
    }
    // ============================================================================
    // Performance Optimization: Cache bid/ask values for fast order placement
    // Avoids re-sorting arrays on every order (saves 3-5ms per order)
    // ============================================================================
    const renderedAt = Date.now();
    (0, state_1.updateState)({
        cachedBidAsk: {
            yesBid,
            yesAsk,
            noBid,
            noAsk,
            timestamp: renderedAt,
        },
    });
    // Keep the rendered asks so an order can price off what the operator was
    // looking at, not off a refresh that landed during their reaction.
    (0, displayed_ask_history_1.recordDisplayedAsks)((0, state_1.getState)().selectedMarket?.id ?? null, yesAsk, noAsk, renderedAt);
    return {
        yesBid,
        yesAsk,
        noBid,
        noAsk,
        lastUpdated: new Date(),
        serverAgeMs,
        display,
    };
}
/**
 * Parse a server display orderbook (pre-bucketed, all four columns).
 * Used by the Kalshi and PM-US polling paths.
 *
 * @param serverAgeMs Age (ms) the server reported for this book, or null when
 *   unavailable. Carried onto BidAskData for the staleness indicator.
 */
function parseDisplayOrderbook(display, serverAgeMs = null) {
    return displayToBidAsk(display, serverAgeMs);
}
/**
 * Parse a raw orderbook response (legacy Polymarket client-side path).
 * YES bid at 7¢ = NO ask at 93¢ (100 - 7)
 * NO bid at 93¢ = YES ask at 7¢ (100 - 93)
 *
 * One-sided books are valid: the missing side's columns come back null.
 */
function parseOrderbook(orderbook) {
    const toLevels = (entries, invert) => {
        const levels = [];
        for (const [price, vol] of entries ?? []) {
            const bps = (0, price_math_1.dollarsToBps)(price);
            if (!Number.isFinite(bps) || !Number.isFinite(vol)) {
                continue;
            }
            levels.push([invert ? (0, price_math_1.invertBps)(bps) : bps, vol]);
        }
        return levels;
    };
    const display = (0, orderbook_display_1.buildDisplayOrderbook)({
        yesBids: toLevels(orderbook.yes_dollars, false),
        yesAsks: toLevels(orderbook.no_dollars, true),
    });
    // The legacy client-side Polymarket path has no server-reported age.
    const result = displayToBidAsk(display, null);
    if (result === null) {
        return null;
    }
    (0, state_1.updateState)({ orderBook: orderbook });
    return result;
}
/**
 * Fetch the pre-bucketed display orderbook for a market, along with the age
 * (ms) the server reports for the underlying book.
 */
async function fetchDisplayOrderbook(marketId) {
    try {
        const response = await (0, api_1.apiRequest)("GET", `/markets/${marketId}/orderbook?format=display`);
        if (!response?.display) {
            return null;
        }
        return {
            display: response.display,
            ageMs: typeof response.ageMs === "number" ? response.ageMs : null,
        };
    }
    catch (error) {
        if (error instanceof api_1.ApiRequestError &&
            (error.statusCode === 404 || error.statusCode === 503)) {
            return null;
        }
        console.error("Failed to fetch orderbook:", error);
        throw error;
    }
}
/**
 * Start polling orderbook data
 * @param marketId - Market ticker
 * @param interval - Poll interval in milliseconds (default: 250ms)
 * @param onUpdate - Callback when data updates
 * @param onError - Callback on error
 * @returns Function to stop polling
 */
function startOrderbookPolling(marketId, interval = 250, onUpdate, onError) {
    let isPolling = true;
    let timeoutId = null;
    // Poll immediately
    async function poll() {
        // Check if stopped before starting async work
        if (!isPolling) {
            return;
        }
        try {
            const result = await fetchDisplayOrderbook(marketId);
            // Check again after async fetch completes
            if (!isPolling) {
                return;
            }
            let bidAskData = null;
            if (result) {
                bidAskData = parseDisplayOrderbook(result.display, result.ageMs);
            }
            // Final check before calling callback
            if (!isPolling) {
                return;
            }
            // Callback handles null to indicate missing or unavailable data.
            onUpdate(bidAskData);
        }
        catch (error) {
            // Don't report errors if polling was stopped
            if (!isPolling) {
                return;
            }
            const err = error instanceof Error ? error : new Error(String(error));
            onError(err);
        }
        // Schedule next poll if still polling
        if (isPolling) {
            timeoutId = window.setTimeout(poll, interval);
        }
    }
    // Start polling
    poll();
    // Return stop function
    return () => {
        isPolling = false;
        if (timeoutId !== null) {
            clearTimeout(timeoutId);
            timeoutId = null;
        }
    };
}
/**
 * Start polling Kalshi orderbook data from the server cache.
 * Subscribes once, then polls the cached data at the requested interval.
 *
 * @param marketId - Kalshi market ticker to subscribe and poll
 * @param interval - Polling interval in milliseconds (defaults to 250ms)
 * @param onUpdate - Callback invoked with parsed bid/ask data or null
 * @param onError - Callback invoked on non-recoverable errors
 */
function startKalshiOrderbookPolling(marketId, interval, onUpdate, onError) {
    let stopPolling = null;
    let isCleaned = false;
    let pendingStop = false;
    (0, kalshi_1.subscribeKalshiOrderbook)([marketId])
        .then(() => {
        if (isCleaned) {
            return;
        }
        const stop = startOrderbookPolling(marketId, interval || DEFAULT_KALSHI_POLLING_INTERVAL_MS, onUpdate, onError);
        if (isCleaned || pendingStop) {
            stop();
            return;
        }
        stopPolling = stop;
    })
        .catch((error) => {
        if (isCleaned) {
            return;
        }
        const err = error instanceof Error ? error : new Error(String(error));
        onError(err);
    });
    return () => {
        isCleaned = true;
        if (stopPolling) {
            stopPolling();
            stopPolling = null;
            return;
        }
        pendingStop = true;
    };
}
/**
 * Start orderbook polling with provider detection.
 * Automatically routes to Kalshi or Polymarket based on current data source.
 *
 * @param marketId Market ID (event_ticker for Kalshi, slug for Polymarket)
 * @param clobTokenIds Optional Polymarket asset IDs [yesTokenId, noTokenId] - required for Polymarket
 * @param interval Poll interval in ms (default 250ms for Kalshi, 500ms for Polymarket)
 * @param onUpdate Callback with bid/ask data
 * @param onError Callback on error
 * @returns Cleanup function to stop polling
 */
function startProviderAwareOrderbookPolling(marketId, clobTokenIds, interval, onUpdate, onError) {
    const market = resolveMarketForPolling(marketId, clobTokenIds);
    console.log(`🟡 Orderbook polling: ${market.venue} market=${marketId} interval=${interval}ms`);
    return (0, registry_1.getVenue)(market.venue).startOrderbook(market, {
        intervalMs: interval,
        onUpdate,
        onError,
    });
}
/**
 * The `Market` for a polling request.
 *
 * Transitional. The adapter takes a market — that is the whole point of the
 * seam (see ai/novig/phase-2-venue-adapter.md §1.3) — but this function's
 * callers still pass a bare id plus extracted `clobTokenIds`. The selected
 * market is the one being polled in every real call, so use it when it matches
 * and synthesize only as a fallback.
 *
 * This goes away when the signature itself takes a Market.
 */
function resolveMarketForPolling(marketId, clobTokenIds) {
    const state = (0, state_1.getState)();
    const selected = state.selectedMarket;
    if (selected && selected.id === marketId) {
        return clobTokenIds && !selected.clobTokenIds
            ? { ...selected, clobTokenIds }
            : selected;
    }
    return {
        id: marketId,
        venue: state.dataSource ?? "kalshi",
        title: marketId,
        subtitle: "",
        strike_price: 0,
        status: "active",
        bid: 0,
        ask: 0,
        clobTokenIds,
    };
}
